import React, { useEffect, useState } from "react";
import { cx } from "class-variance-authority";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { InstantSearch, useSearchBox, Configure } from "react-instantsearch";
import { CloseIcon, Input, ScrollArea } from "@picklebet/ui";
import { Results } from "@/features/search/components/Results/Results";
import { AnimatePresence, motion } from "motion/react";

import * as styles from "./Search.module.scss";

const algoliaClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID as string,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY as string,
);

const searchClient = algoliaClient;

const Search = ({ className }: { className?: string }) => {
  return (
    <InstantSearch
      indexName={`matches`}
      searchClient={searchClient}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <Configure hitsPerPage={8} />
      <SearchBox inputClassName={className} />
      <ScrollArea.Root className={styles.scrollArea} orientation="vertical">
        <ScrollArea.Viewport>
          <Results />
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar thumbClassName={styles.scrollThumb} />
      </ScrollArea.Root>
    </InstantSearch>
  );
};

const SearchBox = ({ inputClassName }: { inputClassName?: string }) => {
  const { query, refine } = useSearchBox();
  const [queryString, setQueryString] = useState(query);

  useEffect(() => {
    if (query !== queryString) {
      setQueryString(query);
    }
  }, [query]);

  const setQuery = (str: string) => {
    setQueryString(str);
    refine(str);
  };

  return (
    <div className={styles.searchBox}>
      <Input
        containerClassName={styles.searchInputContainer}
        wrapperClassName={cx(styles.searchInput, inputClassName, {
          [styles.hasQuery]: !!query,
        })}
        value={queryString}
        autoFocus
        placeholder="Enter search term"
        onChange={(e) => setQuery(e.currentTarget.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck="false"
        icon={
          <AnimatePresence>
            {!!query && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <span onClick={() => setQuery("")} className={styles.clearIcon}>
                  <CloseIcon />
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        }
      />
    </div>
  );
};

export { Search };
